import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, waLink } from "../data/content";
import { EASE } from "./Reveal";

const LINES = ["Find clarity in", "career, love &", "life's next chapter."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: EASE, delay: 0.5 + i * 0.18 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative h-[100svh] w-full overflow-hidden grain"
      data-testid="hero"
    >
      <motion.div style={{ y: imgY, scale }} className="absolute inset-0 -z-0">
        <img
          src={IMAGES.hero}
          alt="Meditation shrine with candlelight"
          className="h-full w-full object-cover object-[68%_center] blur-[1.5px] scale-[1.04]"
        />
        {/* Left-to-right darkening for text readability + bottom grounding */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A050A]/94 via-[#21070D]/70 to-[#21070D]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A050A]/80 via-transparent to-[#1A050A]/30" />
      </motion.div>

      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(55% 50% at 24% 60%, rgba(203,161,53,0.20), transparent 62%)",
        }}
      />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 flex items-center pt-28 sm:pt-32 pb-16"
      >
        <div className="w-full lg:w-[56%] max-w-2xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="flex flex-wrap items-center gap-x-5 sm:gap-x-7 gap-y-2 text-xs sm:text-sm uppercase tracking-[0.34em] text-[#EBD08A]"
            data-testid="hero-eyebrow"
          >
            {["Astrology", "Tarot", "Spiritual Guidance", "Meditation"].map(
              (t, i) => (
                <React.Fragment key={t}>
                  {i > 0 && (
                    <span className="text-[#CBA135]/60 text-[0.9em]">✦</span>
                  )}
                  <span>{t}</span>
                </React.Fragment>
              )
            )}
          </motion.div>

          <h1 className="mt-8 font-serif font-medium text-[#F9F1E2] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight [text-shadow:0_2px_30px_rgba(0,0,0,0.35)]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.2 }}
            className="mt-8 max-w-lg text-[#F4EBD9]/90 text-lg sm:text-xl leading-relaxed body-serif"
          >
            Personalised astrology, tarot and spiritual guidance for career shifts,
            relationship questions, marriage decisions, emotional healing and the
            next 2–3 months ahead.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.4 }}
            className="mt-11 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-book-btn"
              className="btn-gold text-center rounded-full px-9 py-4 text-xs uppercase tracking-[0.22em] font-semibold"
            >
              Book Your Guidance Session
            </a>
            <a
              href="#offerings"
              data-testid="hero-explore-btn"
              className="text-center rounded-full border border-[#EBD08A]/50 text-[#EBD08A] hover:border-[#EBD08A] hover:bg-[#EBD08A]/10 px-9 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors duration-500"
            >
              Explore Sessions
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.7 }}
            className="mt-9 text-xs sm:text-sm text-[#F4EBD9]/65 tracking-wide"
          >
            Private · Compassionate · Practical guidance tailored to your situation
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
